import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Zap } from "lucide-react";
import { useGetBillingUsageQuery, useGetHomeQuery } from "@/store/rest_api";
import { useNavigate } from "react-router-dom";
import { differenceInDays, differenceInHours, format } from "date-fns";
import React from "react";

export function TrialStatusBanner() {
  const { data: user } = useGetHomeQuery();
  const navigate = useNavigate();

  const organisation = user?.activeOrganisation;
  const subscriptionStatus = organisation?.subscriptionStatus;
  const { data: usage } = useGetBillingUsageQuery(undefined, { skip: subscriptionStatus !== "TRIAL" });
  const trialExpirationDate = organisation?.trialExpirationDate;

  const remainingOf = (included: number, used: number) => Math.max(0, Math.round(included - used));

  if (subscriptionStatus !== "TRIAL" || !trialExpirationDate) {
    return null;
  }

  const expirationDate = new Date(trialExpirationDate);
  const now = new Date();
  const daysRemaining = differenceInDays(expirationDate, now);
  const hoursRemaining = differenceInHours(expirationDate, now);
  const isExpiringSoon = daysRemaining <= 3;
  const isExpired = expirationDate < now;

  if (isExpired) {
    return (
      <Alert variant="default">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Trial Expired</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>Your trial has ended. Subscribe to continue using all features.</span>
          <Button onClick={() => navigate("/manage-organisation/plans")} size="sm" variant="outline">
            View Plans
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="rounded-none margin-0 p-0  bg-orange-50 dark:bg-orange-950">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {isExpiringSoon ? (
              <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
            ) : (
              <Zap className="h-5 w-5 text-primary mt-0.5" />
            )}
            <div>
              <h3 className="font-semibold text-lg">{isExpiringSoon ? "Trial Ending Soon" : "Free Trial Active"}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {daysRemaining > 0 ? (
                  <>
                    <strong>{daysRemaining}</strong> day{daysRemaining !== 1 ? "s" : ""} remaining
                  </>
                ) : (
                  <>
                    <strong>{hoursRemaining}</strong> hour{hoursRemaining !== 1 ? "s" : ""} remaining
                  </>
                )}
                {" · "}
                Expires {format(expirationDate, "MMM dd, yyyy")}
              </p>
              {usage && (
                <p className="text-sm text-muted-foreground mt-1">
                  {remainingOf(usage.outboundCallMinutesIncluded, usage.outboundCallMinutesUsed)} of{" "}
                  {usage.outboundCallMinutesIncluded} call minutes
                  {" · "}
                  {remainingOf(usage.outboundSmsIncluded, usage.outboundSmsUsed)} of {usage.outboundSmsIncluded} texts
                  left in your trial
                </p>
              )}
            </div>
          </div>
          <Button onClick={() => navigate("/manage-organisation/plans")} size="sm">
            Activate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
